import { ChartData } from './ai-chart/ai-chart.component';

/**
 * Column definition for table tool output.
 */
export interface TableColumn {
  readonly key: string;
  readonly label: string;
}

/**
 * Output shape for the displayTable tool.
 */
export interface TableData {
  readonly columns: TableColumn[];
  readonly rows: Record<string, unknown>[];
}

/**
 * Single metric card for the displayMetrics tool.
 */
export interface MetricCard {
  readonly label: string;
  readonly value: string | number;
  readonly trend?: 'up' | 'down' | 'flat';
  readonly description?: string;
}

/**
 * Single document card for the displayDocuments tool.
 */
export interface DocumentCard {
  readonly text: string;
  readonly sentiment?: string;
  readonly sentimentScore?: number;
  readonly entities?: string[];
  readonly metadata?: Record<string, unknown>;
}

/**
 * Output shape for the displayDocuments tool.
 */
export interface DocumentsData {
  readonly title: string;
  readonly documents: DocumentCard[];
  readonly totalMatched: number;
}

/**
 * Single verbatim example inside a smart summary.
 */
export interface VerbatimExample {
  readonly text: string;
  readonly sentiment?: string;
}

/**
 * Output shape for the displaySmartSummary tool.
 */
export interface SmartSummaryData {
  readonly title: string;
  readonly summary: string;
  readonly keyFindings: string[];
  readonly verbatimExamples: VerbatimExample[];
}

function normalizeDocumentCard(candidate: unknown): DocumentCard | null {
  if (!candidate || typeof candidate !== 'object' || Array.isArray(candidate)) {
    return null;
  }
  const documentCard = candidate as {
    text?: unknown;
    sentiment?: unknown;
    sentimentScore?: unknown;
    entities?: unknown;
    metadata?: unknown;
  };
  if (typeof documentCard.text !== 'string' || documentCard.text.trim().length === 0) {
    return null;
  }
  const entities = Array.isArray(documentCard.entities)
    ? documentCard.entities.filter((entity): entity is string => typeof entity === 'string')
    : undefined;
  return {
    text: documentCard.text,
    sentiment: typeof documentCard.sentiment === 'string' ? documentCard.sentiment : undefined,
    sentimentScore:
      typeof documentCard.sentimentScore === 'number' ? documentCard.sentimentScore : undefined,
    entities,
    metadata:
      documentCard.metadata &&
      typeof documentCard.metadata === 'object' &&
      !Array.isArray(documentCard.metadata)
        ? (documentCard.metadata as Record<string, unknown>)
        : undefined,
  };
}

export function normalizeDocumentsData(output: unknown): DocumentsData | null {
  if (!output || typeof output !== 'object' || Array.isArray(output)) {
    return null;
  }
  const candidate = output as {
    title?: unknown;
    documents?: unknown;
    totalMatched?: unknown;
  };
  if (typeof candidate.title !== 'string' || candidate.title.trim().length === 0) {
    return null;
  }
  if (!Array.isArray(candidate.documents) || candidate.documents.length === 0) {
    return null;
  }
  const documents = candidate.documents
    .map((documentCard) => normalizeDocumentCard(documentCard))
    .filter((documentCard): documentCard is DocumentCard => documentCard !== null);
  if (documents.length === 0) {
    return null;
  }
  return {
    title: candidate.title,
    documents,
    totalMatched:
      typeof candidate.totalMatched === 'number' ? candidate.totalMatched : documents.length,
  };
}

export function normalizeSmartSummaryData(output: unknown): SmartSummaryData | null {
  if (!output || typeof output !== 'object' || Array.isArray(output)) {
    return null;
  }
  const candidate = output as {
    title?: unknown;
    summary?: unknown;
    keyFindings?: unknown;
    verbatimExamples?: unknown;
  };
  if (typeof candidate.title !== 'string' || candidate.title.trim().length === 0) {
    return null;
  }
  if (typeof candidate.summary !== 'string' || candidate.summary.trim().length === 0) {
    return null;
  }
  if (
    !Array.isArray(candidate.keyFindings) ||
    candidate.keyFindings.length === 0 ||
    candidate.keyFindings.some(
      (finding) => typeof finding !== 'string' || finding.trim().length === 0
    )
  ) {
    return null;
  }
  if (
    !Array.isArray(candidate.verbatimExamples) ||
    candidate.verbatimExamples.some(
      (example) =>
        !example ||
        typeof example !== 'object' ||
        typeof (example as { text?: unknown }).text !== 'string' ||
        (example as { text: string }).text.trim().length === 0
    )
  ) {
    return null;
  }
  return {
    title: candidate.title,
    summary: candidate.summary,
    keyFindings: candidate.keyFindings,
    verbatimExamples: candidate.verbatimExamples as SmartSummaryData['verbatimExamples'],
  };
}

/**
 * Single slice in a comparison result.
 */
export interface ComparisonSlice {
  readonly label: string;
  readonly metrics: Record<string, number>;
}

/**
 * Delta between two slices for a single metric.
 */
export interface ComparisonDelta {
  readonly metric: string;
  readonly sliceA: number;
  readonly sliceB: number;
  readonly delta: number;
  readonly percentChange?: number;
}

/**
 * Output shape for the displayComparison tool.
 */
export interface ComparisonData {
  readonly title: string;
  readonly sliceA: ComparisonSlice;
  readonly sliceB: ComparisonSlice;
  readonly deltas: ComparisonDelta[];
}

/**
 * Single suggested action card.
 */
export interface SuggestedAction {
  readonly label: string;
  readonly description: string;
  readonly actionType: string;
}

/**
 * Output shape for the suggestActions tool.
 */
export interface SuggestActionsData {
  readonly title: string;
  readonly actions: SuggestedAction[];
}

/**
 * Single driver importance result.
 */
export interface DriverResult {
  readonly driver: string;
  readonly importance: number;
  readonly direction: 'positive' | 'negative' | 'neutral';
  readonly explanation: string;
}

/**
 * Output shape for the displayDrivers tool.
 */
export interface DriversData {
  readonly title: string;
  readonly target: string;
  readonly drivers: DriverResult[];
}

/**
 * Discriminated union for all rich tool outputs rendered in chat messages.
 * Add new tool types here as they are implemented.
 */
export type ToolContent =
  | { readonly type: 'chart'; readonly data: ChartData }
  | { readonly type: 'table'; readonly data: TableData }
  | { readonly type: 'metrics'; readonly data: MetricCard[] }
  | { readonly type: 'documents'; readonly data: DocumentsData }
  | { readonly type: 'smartSummary'; readonly data: SmartSummaryData }
  | { readonly type: 'comparison'; readonly data: ComparisonData }
  | { readonly type: 'suggestActions'; readonly data: SuggestActionsData }
  | { readonly type: 'drivers'; readonly data: DriversData };

/**
 * Set of tool names that produce rich content rendered inline.
 * Used by the orchestrator to decide which tool calls to handle.
 */
export const RICH_TOOL_NAMES = new Set<string>([
  'displayChart',
  'displayTable',
  'displayMetrics',
  'displayDocuments',
  'displaySmartSummary',
  'displayComparison',
  'suggestActions',
  'displayDrivers',
]);

/**
 * Maps a backend tool name to its ToolContent type discriminator.
 */
export function toolNameToContentType(toolName: string): ToolContent['type'] | null {
  switch (toolName) {
    case 'displayChart':
      return 'chart';
    case 'displayTable':
      return 'table';
    case 'displayMetrics':
      return 'metrics';
    case 'displayDocuments':
      return 'documents';
    case 'displaySmartSummary':
      return 'smartSummary';
    case 'displayComparison':
      return 'comparison';
    case 'suggestActions':
      return 'suggestActions';
    case 'displayDrivers':
      return 'drivers';
    default:
      return null;
  }
}
